import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Grid,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  useMediaQuery,
  Fab,
  Zoom,
  useScrollTrigger
} from '@mui/material';
import {
  Palette,
  ContentCopy,
  Refresh,
  Download,
  Favorite,
  FavoriteBorder,
  Share,
  FormatColorFill,
  Gradient,
  Contrast,
  ColorLens,
  AutoAwesome,
  Shuffle,
  Add,
  Remove,
  KeyboardArrowUp
} from '@mui/icons-material';

// Color harmony algorithms
const colorHarmonies = {
  complementary: (baseColor) => {
    const hsl = hexToHsl(baseColor);
    return [baseColor, hslToHex((hsl.h + 180) % 360, hsl.s, hsl.l)];
  },
  analogous: (baseColor) => {
    const hsl = hexToHsl(baseColor);
    return [
      baseColor,
      hslToHex((hsl.h + 30) % 360, hsl.s, hsl.l),
      hslToHex((hsl.h - 30) % 360, hsl.s, hsl.l)
    ];
  },
  triadic: (baseColor) => {
    const hsl = hexToHsl(baseColor);
    return [
      baseColor,
      hslToHex((hsl.h + 120) % 360, hsl.s, hsl.l),
      hslToHex((hsl.h + 240) % 360, hsl.s, hsl.l)
    ];
  },
  tetradic: (baseColor) => {
    const hsl = hexToHsl(baseColor);
    return [
      baseColor,
      hslToHex((hsl.h + 90) % 360, hsl.s, hsl.l),
      hslToHex((hsl.h + 180) % 360, hsl.s, hsl.l),
      hslToHex((hsl.h + 270) % 360, hsl.s, hsl.l)
    ];
  },
  monochromatic: (baseColor) => {
    const hsl = hexToHsl(baseColor);
    return [
      hslToHex(hsl.h, hsl.s, Math.min(95, hsl.l + 30)),
      hslToHex(hsl.h, hsl.s, hsl.l),
      hslToHex(hsl.h, hsl.s, Math.max(5, hsl.l - 30))
    ];
  }
};

// Utility functions
const hexToHsl = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
};

const hslToHex = (h, s, l) => {
  h /= 360;
  s /= 100;
  l /= 100;
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = (x) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const getContrastColor = (hexColor) => {
  const r = parseInt(hexColor.substr(1, 2), 16);
  const g = parseInt(hexColor.substr(3, 2), 16);
  const b = parseInt(hexColor.substr(5, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

function ScrollTop({ children }) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top-anchor',
    );
    if (anchor) {
      anchor.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1000,
        }}
      >
        {children}
      </Box>
    </Zoom>
  );
}

function ColorPaletteGenerator() {
  const [baseColor, setBaseColor] = useState('#1976d2');
  const [harmonyType, setHarmonyType] = useState('complementary');
  const [palette, setPalette] = useState([]);
  const [savedPalettes, setSavedPalettes] = useState([]);
  const [numberOfColors, setNumberOfColors] = useState(5);
  const [customColors, setCustomColors] = useState(['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#7b1fa2']);
  const [activeTab, setActiveTab] = useState('harmony');
  const [copiedColor, setCopiedColor] = useState('');
  const [exportDialog, setExportDialog] = useState(false);
  const [gradientColors, setGradientColors] = useState(['#1976d2', '#d32f2f']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Generate palette when base color or harmony type changes
  useEffect(() => {
    generateHarmonyPalette();
  }, [baseColor, harmonyType]);

  const generateHarmonyPalette = () => {
    let newPalette;
    
    if (harmonyType === 'custom') {
      newPalette = customColors.slice(0, numberOfColors);
    } else if (harmonyType === 'gradient') {
      newPalette = generateGradientPalette();
    } else {
      newPalette = colorHarmonies[harmonyType](baseColor);
      // Extend the palette if needed
      while (newPalette.length < numberOfColors && harmonyType !== 'monochromatic') {
        const hsl = hexToHsl(newPalette[newPalette.length - 1]);
        newPalette.push(hslToHex((hsl.h + 30) % 360, hsl.s, hsl.l));
      }
    }
    
    setPalette(newPalette);
  };

  const generateGradientPalette = () => {
    const colors = [];
    for (let i = 0; i < numberOfColors; i++) {
      const ratio = i / (numberOfColors - 1);
      const color = interpolateColor(gradientColors[0], gradientColors[1], ratio);
      colors.push(color);
    }
    return colors;
  };

  const interpolateColor = (color1, color2, factor) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color1);
    const r1 = parseInt(result[1], 16);
    const g1 = parseInt(result[2], 16);
    const b1 = parseInt(result[3], 16);

    const result2 = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color2);
    const r2 = parseInt(result2[1], 16);
    const g2 = parseInt(result2[2], 16);
    const b2 = parseInt(result2[3], 16);

    const r = Math.round(r1 + (r2 - r1) * factor);
    const g = Math.round(g1 + (g2 - g1) * factor);
    const b = Math.round(b1 + (b2 - b1) * factor);

    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  const generateRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  const generateRandomPalette = () => {
    const randomColor = generateRandomColor();
    setBaseColor(randomColor);
  };

  const copyToClipboard = async (color) => {
    try {
      await navigator.clipboard.writeText(color);
      setCopiedColor(color);
      setTimeout(() => setCopiedColor(''), 2000);
    } catch (err) {
      console.error('Failed to copy color: ', err);
    }
  };

  const savePalette = () => {
    const newPalette = {
      id: Date.now(),
      colors: palette,
      name: `Palette ${savedPalettes.length + 1}`,
      date: new Date().toLocaleDateString()
    };
    setSavedPalettes(prev => [newPalette, ...prev].slice(0, 10)); // Keep last 10
  };

  const exportPalette = () => {
    const paletteData = {
      name: 'Generated Palette',
      colors: palette,
      harmony: harmonyType,
      baseColor: baseColor,
      generated: new Date().toISOString()
    };
    
    const dataStr = JSON.stringify(paletteData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `palette-${Date.now()}.json`;
    link.click();
  };

  const updateCustomColor = (index, color) => {
    const newColors = [...customColors];
    newColors[index] = color;
    setCustomColors(newColors);
    if (harmonyType === 'custom') {
      generateHarmonyPalette();
    }
  };

  const addCustomColor = () => {
    setCustomColors(prev => [...prev, generateRandomColor()]);
  };

  const removeCustomColor = (index) => {
    if (customColors.length > 2) {
      setCustomColors(prev => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <div id="back-to-top-anchor" />
      <br/><br/><br/>
      {/* Header */}
      <Box textAlign="center" mb={6}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
          <Palette sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" fontWeight="bold" color="primary">
            Color Palette Generator
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Create beautiful color palettes and harmonies for your design projects
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Controls Panel */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, position: 'sticky', top: 24 }}>
            {/* Base Color Picker */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <FormatColorFill color="primary" />
                Base Color
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <input
                  type="color"
                  value={baseColor}
                  onChange={(e) => setBaseColor(e.target.value)}
                  style={{
                    width: 60,
                    height: 60,
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer'
                  }}
                />
                <Box flex={1}>
                  <TextField
                    fullWidth
                    value={baseColor}
                    onChange={(e) => setBaseColor(e.target.value)}
                    size="small"
                  />
                </Box>
                <Tooltip title="Random Color">
                  <IconButton onClick={generateRandomPalette}>
                    <Shuffle />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Harmony Type */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <AutoAwesome color="primary" />
                Color Harmony
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Harmony Type</InputLabel>
                <Select
                  value={harmonyType}
                  onChange={(e) => setHarmonyType(e.target.value)}
                  label="Harmony Type"
                >
                  <MenuItem value="complementary">Complementary</MenuItem>
                  <MenuItem value="analogous">Analogous</MenuItem>
                  <MenuItem value="triadic">Triadic</MenuItem>
                  <MenuItem value="tetradic">Tetradic</MenuItem>
                  <MenuItem value="monochromatic">Monochromatic</MenuItem>
                  <MenuItem value="gradient">Gradient</MenuItem>
                  <MenuItem value="custom">Custom Colors</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Number of Colors */}
            <Box mb={3}>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <ColorLens color="primary" />
                Number of Colors: {numberOfColors}
              </Typography>
              <Slider
                value={numberOfColors}
                onChange={(e, value) => setNumberOfColors(value)}
                min={2}
                max={8}
                step={1}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* Gradient Colors */}
            {harmonyType === 'gradient' && (
              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>
                  Gradient Colors
                </Typography>
                <Grid container spacing={1}>
                  {gradientColors.map((color, index) => (
                    <Grid item xs={6} key={index}>
                      <Box display="flex" gap={1} alignItems="center">
                        <input
                          type="color"
                          value={color}
                          onChange={(e) => {
                            const newColors = [...gradientColors];
                            newColors[index] = e.target.value;
                            setGradientColors(newColors);
                          }}
                          style={{
                            width: 40,
                            height: 40,
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer'
                          }}
                        />
                        <TextField
                          value={color}
                          onChange={(e) => {
                            const newColors = [...gradientColors];
                            newColors[index] = e.target.value;
                            setGradientColors(newColors);
                          }}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Custom Colors */}
            {harmonyType === 'custom' && (
              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom display="flex" justifyContent="space-between">
                  Custom Colors
                  <Tooltip title="Add Color">
                    <IconButton size="small" onClick={addCustomColor}>
                      <Add />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <Grid container spacing={1}>
                  {customColors.map((color, index) => (
                    <Grid item xs={6} key={index}>
                      <Box display="flex" gap={1} alignItems="center">
                        <input
                          type="color"
                          value={color}
                          onChange={(e) => updateCustomColor(index, e.target.value)}
                          style={{
                            width: 40,
                            height: 40,
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer'
                          }}
                        />
                        <TextField
                          value={color}
                          onChange={(e) => updateCustomColor(index, e.target.value)}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        {customColors.length > 2 && (
                          <Tooltip title="Remove Color">
                            <IconButton size="small" onClick={() => removeCustomColor(index)}>
                              <Remove />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Action Buttons */}
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={generateRandomPalette}
                fullWidth={isMobile}
              >
                Randomize
              </Button>
              <Button
                variant="outlined"
                startIcon={<FavoriteBorder />}
                onClick={savePalette}
                fullWidth={isMobile}
              >
                Save
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => setExportDialog(true)}
                fullWidth={isMobile}
              >
                Export
              </Button>
            </Box>
          </Paper>

          {/* Saved Palettes */}
          {savedPalettes.length > 0 && (
            <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Saved Palettes
              </Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {savedPalettes.map((savedPalette) => (
                  <Card key={savedPalette.id} sx={{ mb: 1, cursor: 'pointer' }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="body2" fontWeight="bold">
                        {savedPalette.name}
                      </Typography>
                      <Box display="flex" height={20} borderRadius={1} overflow="hidden">
                        {savedPalette.colors.map((color, index) => (
                          <Box
                            key={index}
                            sx={{
                              flex: 1,
                              backgroundColor: color,
                              borderRight: index < savedPalette.colors.length - 1 ? '1px solid white' : 'none'
                            }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {savedPalette.date}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Palette Display */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom textAlign="center">
              Generated Palette
            </Typography>
            
            {copiedColor && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Copied {copiedColor} to clipboard!
              </Alert>
            )}

            {/* Color Palette */}
            <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
              {palette.map((color, index) => (
                <Box
                  key={index}
                  sx={{
                    height: 80,
                    backgroundColor: color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      zIndex: 1
                    }
                  }}
                  onClick={() => copyToClipboard(color)}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: getContrastColor(color),
                      fontWeight: 'bold',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                    }}
                  >
                    {color}
                  </Typography>
                  <Tooltip title="Copy color code">
                    <IconButton
                      sx={{
                        color: getContrastColor(color),
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.3)'
                        }
                      }}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>

            {/* Color Chips */}
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center">
              {palette.map((color, index) => (
                <Tooltip key={index} title={`Click to copy ${color}`}>
                  <Chip
                    label={color}
                    sx={{
                      backgroundColor: color,
                      color: getContrastColor(color),
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                    onClick={() => copyToClipboard(color)}
                  />
                </Tooltip>
              ))}
            </Box>

            {/* Preview Section */}
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Palette Preview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card sx={{ bgcolor: palette[0] || '#f5f5f5', color: getContrastColor(palette[0] || '#f5f5f5') }}>
                    <CardContent>
                      <Typography variant="h6">Primary Color</Typography>
                      <Typography variant="body2">Use for main actions and key elements</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card sx={{ bgcolor: palette[1] || '#f5f5f5', color: getContrastColor(palette[1] || '#f5f5f5') }}>
                    <CardContent>
                      <Typography variant="h6">Secondary Color</Typography>
                      <Typography variant="body2">Use for secondary actions and accents</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Export Dialog */}
      <Dialog open={exportDialog} onClose={() => setExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Color Palette</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Choose how you want to export your color palette:
          </Typography>
          <Box display="flex" gap={2} flexDirection="column">
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={exportPalette}
              size="large"
            >
              Export as JSON
            </Button>
            <Button
              variant="outlined"
              startIcon={<Share />}
              onClick={() => {
                const text = `Color Palette: ${palette.join(', ')}`;
                navigator.clipboard.writeText(text);
                setExportDialog(false);
              }}
              size="large"
            >
              Copy as Text
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Scroll to top button */}
      <ScrollTop>
        <Fab color="primary" size="medium" aria-label="scroll back to top">
          <KeyboardArrowUp />
        </Fab>
      </ScrollTop>
    </Container>
  );
}

export default ColorPaletteGenerator;